import {
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    DialogTitle,
    Link,
} from '@fluentui/react-components';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { WarningImage } from '../../icons/status';
import { useAppStyles } from '../../styles';
import { TrackContext } from '../common';
import { CancelButton, CloseButton, ConfirmButton, ConfirmationField } from '../dialogs';

// Delete account dialog properties.
export type DeleteAccountDialogProps = Readonly<
    Partial<DialogProps> & {
        onDownload: () => void;
        onDelete: () => void;
    }
>;

// Delete account confirmation dialog.
export default (props: DeleteAccountDialogProps) => {
    const { onDownload, onDelete, open } = props;
    const [confirmed, setConfirmed] = useState(false);
    const styles = useAppStyles();

    return (
        <TrackContext.Provider value={{ container: 'delete-account-dialog' }}>
            <Dialog {...props}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle action={<CloseButton tabIndex={-1} />}>
                            <FormattedMessage
                                description="Delete account dialog title."
                                defaultMessage="Delete account"
                            />
                        </DialogTitle>
                        <DialogContent className={styles.flexRowCentered}>
                            <WarningImage className={styles.dialogImage} />
                            <div className={styles.dialogContent}>
                                <div>
                                    <FormattedMessage
                                        description="Confirmation text on deleting user account."
                                        defaultMessage="Are you sure you want to delete your account?"
                                    />
                                </div>
                                <div>
                                    <FormattedMessage
                                        description="Title for a list of items that will be lost when deleting user account."
                                        defaultMessage="All of your data will be deleted including:"
                                    />
                                    <ul>
                                        <li>
                                            <FormattedMessage
                                                description="An item in the list of things that will be lost when deleting user account."
                                                defaultMessage="All progress towards completion of any courses you have started"
                                            />
                                        </li>
                                        <li>
                                            <FormattedMessage
                                                description="An item in the list of things that will be lost when deleting user account."
                                                defaultMessage="All code you have written for the courses"
                                            />
                                        </li>
                                        <li>
                                            <FormattedMessage
                                                description="An item in the list of things that will be lost when deleting user account."
                                                defaultMessage="All your projects including code and published web sites"
                                            />
                                        </li>
                                    </ul>
                                    <div className={styles.textSemibold}>
                                        <FormattedMessage
                                            description="Bolded warning text on deleting user account."
                                            defaultMessage="This operation cannot be undone!"
                                        />
                                    </div>
                                </div>
                                <br />
                                <div>
                                    <FormattedMessage
                                        description="Message on the delete account dialog. DOWNLOAD_LINK is a link to download user's data with text 'Click here'."
                                        defaultMessage="{DOWNLOAD_LINK} to download your code and data before it is deleted."
                                        values={{
                                            DOWNLOAD_LINK: (
                                                <Link onClick={onDownload}>
                                                    <FormattedMessage
                                                        description="Link text on the delete account dialog to download user's code."
                                                        defaultMessage="Click here"
                                                    />
                                                </Link>
                                            ),
                                        }}
                                    />
                                </div>
                                <ConfirmationField
                                    dialogOpen={open}
                                    onChange={setConfirmed}
                                    confirmationText="DELETE ACCOUNT"
                                />
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <CancelButton />
                            <ConfirmButton className={styles.buttonDanger} onClick={onDelete} disabled={!confirmed}>
                                <FormattedMessage
                                    description="Delete account dialog confirmation button text."
                                    defaultMessage="Delete account"
                                />
                            </ConfirmButton>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </TrackContext.Provider>
    );
};
